import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FieldErrors, UseFormRegister } from "react-hook-form";
import {
  FIELD_COLORS,
  LAYER_COLORS,
} from "../../../constants/design/colors";
import { SPACING } from "../../../constants/design/spacing";
import { ChainIdEnum } from "../../../enums/chain";
import {
  getAssetLogo,
  getAssetShortName,
  getCollateralAssets,
} from "../../../utils/asset/assets";
import { Chevron } from "../../shared/Chevron/style";
import Dropdown from "../../shared/Dropdown";
import { Input } from "../../shared/Input";
import { MaxButton } from "../../shared/NumericField";
import {
  DepositAssetDropdownItem,
  DepositAssetDropdownTitle,
  InputContainer,
  InputError,
  InputLabel,
} from "../style";
import { AllCollaterals } from "../../../constants/addresses";
import {
  isValidDecimalInput,
  toTokenUnits,
} from "../../../utils/decimalString";
import { invalidAmountMessage } from "../WithdrawModal.helpers";
import { IFormFieldValues } from "./InitialStateModal.types";

interface IAmountFieldProps {
  t: TFunction;
  formError: TFunction;
  register: UseFormRegister<IFormFieldValues>;
  errors: FieldErrors<IFormFieldValues>;
  asset: AllCollaterals;
  setAsset: (asset: AllCollaterals) => void;
  chainId: number | undefined;
  assetDropdownOpen: boolean;
  setAssetDropdownOpen: (open: boolean) => void;
  onMaxClick: () => void;
  transferrableBalance: string | undefined;
  /** Why no maximum can be offered, when there is none. */
  transferUnavailableReason?: string;
  assetDecimals: number;
}

function AmountField({
  t,
  formError,
  register,
  errors,
  asset,
  setAsset,
  chainId,
  assetDropdownOpen,
  setAssetDropdownOpen,
  onMaxClick,
  transferrableBalance,
  transferUnavailableReason,
  assetDecimals,
}: IAmountFieldProps) {
  const { t: inputsT } = useTranslation("app", { keyPrefix: "common.inputs" });
  return (
    <InputContainer>
      <InputLabel>{t("amount")}</InputLabel>
      <Input
        placeholder="0.00"
        leftAccessory={
          <Dropdown
            onToggle={setAssetDropdownOpen}
            toggleStyles={{
              padding: `0px ${SPACING.one}px`,
              backgroundColor: FIELD_COLORS.two,
            }}
            dropdownMenuContainerStyles={{
              overflow: "hidden",
              backgroundColor: LAYER_COLORS.three,
            }}
            title={
              <DepositAssetDropdownTitle>
                <img
                  src={getAssetLogo(asset)}
                  width={24}
                  height={24}
                  alt={asset}
                />
                <Chevron
                  size="small"
                  direction={assetDropdownOpen ? "up" : "down"}
                />
              </DepositAssetDropdownTitle>
            }
            items={getCollateralAssets(chainId as ChainIdEnum, "transfer").map((v) => ({
              label: (
                <DepositAssetDropdownItem selected={v === asset}>
                  <img
                    src={getAssetLogo(v)}
                    width={24}
                    height={24}
                    alt={v}
                  />
                  <span>{getAssetShortName(v)}</span>
                </DepositAssetDropdownItem>
              ),
              onSelect: () => {
                setAsset(v);
              },
            }))}
          />
        }
        rightAccessory={
          <MaxButton onClick={onMaxClick} type="button"
            disabled={transferrableBalance === undefined}
            title={
              transferUnavailableReason
                ? inputsT(`max_unavailable.${transferUnavailableReason}`)
                : undefined
            }
          >
            {inputsT("max")}
          </MaxButton>
        }
        type="text"
        inputMode="decimal"
        {...register("amount", {
          required: true,
          // type="text": `parseFloat` accepted "1.5abc" and "1,000" (as 1).
          validate: {
            validDecimal: (v) => isValidDecimalInput(v, assetDecimals),
            moreThanZero: (v) =>
              Boolean(toTokenUnits(v, assetDecimals)?.gt(0)),
            lessThanEqualToTransferableBalance: (v) => {
              const amount = toTokenUnits(v, assetDecimals);
              const max = toTokenUnits(transferrableBalance, assetDecimals);
              return Boolean(amount && max && amount.lte(max));
            },
          },
        })}
        error={!!errors.amount?.type}
      />
      {errors.amount?.type === "required" && (
        <InputError>{formError("address.required")}</InputError>
      )}
      {errors.amount?.type === "validDecimal" && (
        <InputError>{invalidAmountMessage(t, assetDecimals)}</InputError>
      )}
      {errors.amount?.type === "moreThanZero" && (
        <InputError>{formError("address.moreThanZero")}</InputError>
      )}
      {errors.amount?.type === "lessThanEqualToTransferableBalance" && (
        <InputError>
          {formError("address.lessThanEqualToTransferableBalance")}
        </InputError>
      )}
    </InputContainer>
  );
}

export default AmountField;
